/**
 * Brain data layer — React Query over the memory-tree routes.
 *
 * Same arrangement as the wiki's (`components/wiki/queries.ts`): the RQ cache is
 * the cache, so revisiting a file is instant and a failure SURFACES as isError
 * instead of being swallowed into an empty state that would read as "your brain
 * is empty".
 */
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { worldsOfEntries } from "@bash/shared";
import { useAuth } from "@/contexts/AuthContext";
import { resilientFetch } from "@/lib/fetch";
import type {
  NamiBrainSaveRefusal,
  NamiEvidenceCard,
  NamiEvidenceListing,
  NamiOpenLoops,
  NamiPageProof,
  NamiSelfUpdates,
  NamiTreeBacklinks,
  NamiTreeFile,
  NamiTreeListing,
  NamiTreeSearchResults,
} from "./brainTypes";

const SERVER_URL =
  process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";

/**
 * The map — the page the room opens on, and the one the app shell warms.
 *
 * It is a COMPILED ROOT: the dream writes it, and it has no manifest entry of
 * its own, so whether a brain has one is the file route's answer and no other's.
 * It lives here rather than in the room because the warmup needs the name
 * without needing the room.
 */
export const BRAIN_INDEX = "knowledge/index.md";

export const brainKeys = {
  tree: (userId: string | null) => ["brain", "tree", userId] as const,
  file: (userId: string | null, path: string) =>
    ["brain", "file", userId, path] as const,
  evidence: (userId: string | null, cite: string) =>
    ["brain", "evidence", userId, cite] as const,
  search: (userId: string | null, query: string) =>
    ["brain", "search", userId, query] as const,
  backlinks: (userId: string | null, path: string) =>
    ["brain", "backlinks", userId, path] as const,
};

/** The front door's own reads (task D6/W1), keyed beside the others so one
 *  place says what this room caches and one helper says what a write drops. */
export const brainDoorKeys = {
  pageProof: (userId: string | null, path: string) =>
    ["brain", "page-proof", userId, path] as const,
  gallery: (userId: string | null, doors: string, limit: number) =>
    ["brain", "gallery", userId, doors, limit] as const,
  loops: (userId: string | null) => ["brain", "loops", userId] as const,
  selfUpdates: (userId: string | null) =>
    ["brain", "self-updates", userId] as const,
  thumb: (userId: string | null, cite: string) =>
    ["brain", "thumb", userId, cite] as const,
};

/**
 * WHICH WRITE JUST LANDED (Ruling 312). Three, and they reach different
 * distances into what this room is showing.
 */
export type BrainWrite = "save" | "forget" | "dream";

/**
 * WHAT A WRITE LEAVES BEHIND — ONE HELPER, ONE MAPPING (Ruling 312).
 *
 * There were two lists and they had drifted: a page edit dropped the file, the
 * map, the listing, search and backlinks but NOT the page's proof, so a
 * citation the reader had just removed stayed as a chip until they reloaded;
 * and a forget and a dream dropped all of those but never the open loops or the
 * gallery, so a dream that closed a thread left the band's badge and the
 * "Waiting on you" section wrong until the tab was reopened (their observers
 * stay mounted, so `staleTime` never expires them).
 *
 * THE FLOOR IS THE HEAD. All three writes advance and republish it, so
 * everything read off it is behind: the pages named, the map, the listing every
 * count on this surface comes from — `lastDream` rides that same answer — and
 * the two published-head indexes, and the proof behind every page.
 *
 * A FORGET AND A DREAM REACH FURTHER. A forget by cite takes lines off pages
 * the reader was not looking at and can close nothing, but it removes a piece
 * of evidence, which is a gallery row; a dream can rewrite any page and open or
 * close any thread. A save cannot do either.
 */
export function useInvalidateBrain() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const qc = useQueryClient();
  return async (after: BrainWrite, paths: readonly string[] = []) => {
    const drop = (queryKey: readonly unknown[]) =>
      void qc.invalidateQueries({ queryKey });
    // AWAITED, and only these: a caller that awaits the write is waiting for
    // the page it just wrote to be refetched, not for the whole room.
    //
    // THE PAGE AND ITS PROOF TOGETHER (brain articles, 2026-10-06). The proof
    // used to be dropped only once the page had landed, so a cite the reader had
    // just added was numbered against the old proof for a whole round trip.
    await Promise.all(
      paths.flatMap((path) => [
        qc.invalidateQueries({ queryKey: brainKeys.file(userId, path) }),
        qc.invalidateQueries({
          queryKey: brainDoorKeys.pageProof(userId, path),
        }),
      ])
    );
    drop(brainKeys.file(userId, BRAIN_INDEX));
    drop(brainKeys.tree(userId));
    drop(["brain", "search", userId]);
    drop(["brain", "backlinks", userId]);
    // Every other page's proof (a forget reaches pages the reader is not on),
    // but not the ones just refetched above: a second drop would refetch them
    // again.
    void qc.invalidateQueries({
      queryKey: ["brain", "page-proof", userId],
      predicate: (query) => !paths.includes(String(query.queryKey[3])),
    });
    drop(brainDoorKeys.selfUpdates(userId)); // every write can change a self page
    if (after !== "save") {
      drop(["brain", "gallery", userId]);
      drop(brainDoorKeys.loops(userId));
    }
  };
}

function useAuthHeaders() {
  const { getAccessToken } = useAuth();
  return async (): Promise<Record<string, string>> => {
    const token = await getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
}

/** The same headers, for the one caller outside this file — the dream trigger,
 *  which polls and posts rather than subscribing and so is not a query. */
export const useBrainAuthHeaders = useAuthHeaders;

/**
 * A read that came back with a status, carried as a FIELD rather than a message
 * (Ruling 228). The retry rule below has to tell a verdict from a blip, and the
 * status is the only thing that says which — read off a string it would be one
 * regex away from being wrong about a path with `400` in its name.
 */
class BrainReadError extends Error {
  constructor(readonly status: number) {
    super(`brain fetch failed: ${status}`);
    this.name = "BrainReadError";
  }
}

async function getJson<T>(
  url: string,
  headers: Record<string, string>
): Promise<T> {
  const res = await resilientFetch(url, { headers });
  if (!res.ok) throw new BrainReadError(res.status);
  return (await res.json()) as T;
}

/**
 * One more try, unless the answer was a VERDICT (Ruling 228).
 *
 * A 5xx or a dropped connection is a blip and a second attempt often lands. A 4xx
 * is not: the route has ruled on the request — a path outside the tree, a token
 * that is not a citation — and asking again gets the same answer, at the cost of a
 * second round trip and a second error line in the feed for every bad link a
 * reader ever follows.
 */
const retryUnlessRefused = (failures: number, error: unknown): boolean =>
  failures < 1 &&
  !(
    error instanceof BrainReadError &&
    error.status >= 400 &&
    error.status < 500
  );

/**
 * THE LISTING IS THE EXPENSIVE ONE, so it is held.
 *
 * A warm read of a real brain measures about 560 ms — 816 rows, enriched — and
 * every section of the front door reads it: the band's counts, Home's tables, a
 * world's list, the crumb bar's name for an open page. Refetching per view would
 * put half a second between a reader and every pill they press, for an answer
 * that changes once a night.
 *
 * FIVE MINUTES IS SAFE BECAUSE THE WRITES INVALIDATE. A page edit advances the
 * head and `useSaveBrainPage` invalidates this key by hand, so the only thing the
 * staleTime governs is how long a dream that landed mid-session takes to show —
 * and that is a nightly event, not an interactive one.
 */
export function useBrainTree() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  return useQuery<NamiTreeListing>({
    queryKey: brainKeys.tree(userId),
    queryFn: async () =>
      withWorlds(
        await getJson(`${SERVER_URL}/api/nami/tree`, await getHeaders())
      ),
    enabled: !!userId,
    staleTime: 5 * 60_000,
    retry: retryUnlessRefused,
  });
}

/**
 * A LISTING FROM A SERVER THAT PREDATES `worlds`, ANSWERED FROM ITS OWN ROWS
 * (Ruling 330, F1).
 *
 * The field landed with W4 and the reader was written against it: no `worlds` reads
 * as a brain with no folders, so the band shows four openings and Proof, Browse is
 * empty, and every world's deep link falls to Home. That is not a hypothetical —
 * `deploy-web` runs AFTER the server on a promote, so it is exactly what a reader
 * loaded before the web deploy sees, and it is what a pinned staging serves all day.
 *
 * DERIVED WITH THE SERVER'S OWN FUNCTION, from the rows this answer carries: every
 * row already names the world it is in, so the set is recoverable here and it is
 * recoverable the same way. Not a guess and not a second rule — literally the call
 * the server makes.
 *
 * ONLY WHEN THE KEY IS ABSENT, which is the difference between a server that cannot
 * answer and a server that has. `worlds: []` is an answer — a W4 server over a brain
 * with no knowledge pages sends exactly that — and overriding it would be the reader
 * arguing with the reply it asked for.
 *
 * AND `[]` MEANS SOMETHING ELSE ONE LAYER DOWN, which is not a contradiction: this
 * decides what came off the WIRE, and `brainSections` decides what to DRAW for a
 * caller holding no worlds. An empty array survives this function and reaches that
 * one, which paints the seeded band at no count — the right screen for an empty
 * brain, and the same screen a listing still in flight gets.
 */
function withWorlds(listing: NamiTreeListing): NamiTreeListing {
  if (listing.worlds !== undefined) return listing;
  return { ...listing, worlds: worldsOfEntries(listing.entries ?? []) };
}

/**
 * ONE PAGE, READ ONCE.
 *
 * Two components asking `useBrainFile` for the same path is not free, and the way
 * it is not free is subtle: React Query answers a second subscriber from cache
 * while the read SUCCEEDED, but a query in an error state is refetched when a new
 * observer mounts (`retryOnMount`). So a room and the page inside it both calling
 * this turned one refused path into two round trips — the exact thing Ruling 228
 * forbids. The room holds the one call and hands the answer down as this shape.
 */
export interface BrainFileRead {
  data?: NamiTreeFile;
  isPending: boolean;
  isError: boolean;
}

export function useBrainFile(path: string | null) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  return useQuery<NamiTreeFile>({
    queryKey: brainKeys.file(userId, path ?? ""),
    queryFn: async () =>
      getJson(
        `${SERVER_URL}/api/nami/tree/file?path=${encodeURIComponent(path ?? "")}`,
        await getHeaders()
      ),
    enabled: !!userId && !!path,
    staleTime: 60_000,
    retry: retryUnlessRefused,
  });
}

/**
 * How a save ended. STATUS-DERIVED, never a message read off the wire: each of
 * these asks the reader for a different thing, and only the server knows which.
 *
 *  · `stale`   — the head moved. RELOAD; the copy in hand is a fork.
 *  · `busy`    — a dream is holding the brain. The same words, in a moment.
 *  · `invalid` — the page would not pass the gate as written.
 *  · `tooLong` — the STORED page is longer than a read can serve whole, so this
 *                surface cannot edit it at all. Nothing about the writing is wrong.
 *  · `failed`  — anything else. The words are kept and the save can be retried.
 */
export type BrainSaveOutcome =
  "saved" | "stale" | "busy" | "invalid" | "tooLong" | "failed";

/**
 * What a save came back with. An OBJECT rather than a bare outcome because the two
 * refusals the reader can act on carry a fact with them: which gate rule refused,
 * and which head the server holds now.
 */
export interface BrainSaveResult {
  outcome: BrainSaveOutcome;
  /** `invalid` only: the FIRST rule the gate refused on — the rule the room names. */
  rule?: string;
  /** `stale` only, when the server knew it: the head it holds now — the wire's own
   *  `currentSha`, whose shape and whose future are named in brainTypes. */
  currentSha?: string;
}

/**
 * Put a person's own words on their brain's head (task D4).
 *
 * `baseSha` is the head the page was READ at and the caller owns it — it is not
 * read back out of the cache here, because a save that quietly re-based itself on
 * whatever the head became would overwrite exactly the change the reader was told
 * about.
 *
 * A LANDED SAVE AND A STALE ONE BOTH INVALIDATE. In both cases the copy in hand is
 * behind the head: one because this edit advanced it, the other because something
 * else did. The map is refreshed with them — every page edit recompiles it.
 */
export function useSaveBrainPage() {
  const invalidate = useInvalidateBrain();
  const getHeaders = useAuthHeaders();
  return async (edit: {
    path: string;
    content: string;
    baseSha: string;
  }): Promise<BrainSaveResult> => {
    let result: BrainSaveResult;
    try {
      const res = await resilientFetch(`${SERVER_URL}/api/nami/tree/page`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(await getHeaders()),
        },
        body: JSON.stringify(edit),
      });
      // Read once: two of the four answers carry a fact the room needs, and none of
      // them is read out of prose — the route sends structured fields for exactly
      // this reason.
      const body =
        res.ok || res.status < 400
          ? null
          : ((await res
              .json()
              .catch(() => null)) as NamiBrainSaveRefusal | null);
      if (res.ok) result = { outcome: "saved" };
      // Two different 422s, told apart by the STRUCTURED `error` the route sends:
      // one is about the words, the other about the page they are on.
      else if (res.status === 422 && body?.error === "too_long_to_edit")
        result = { outcome: "tooLong" };
      else if (res.status === 422)
        result = { outcome: "invalid", rule: body?.errors?.[0]?.rule };
      // Two different 409s, told apart by the STRUCTURED field the route sends: one
      // says reload, the other says wait.
      else if (res.status === 409 && body?.error === "brain_busy")
        result = { outcome: "busy" };
      else if (res.status === 409)
        result = { outcome: "stale", currentSha: body?.currentSha };
      else result = { outcome: "failed" };
    } catch {
      result = { outcome: "failed" };
    }
    // A save advances and publishes the HEAD, and the front door reads that same
    // head: a renamed page answers to a new name, its neighbours' excerpts change
    // with it, and the proof under it is re-resolved. ONE helper says what that
    // means (Ruling 312), so a second write cannot answer differently.
    if (result.outcome === "saved" || result.outcome === "stale")
      await invalidate("save", [edit.path]);
    return result;
  };
}

/**
 * The proof behind one claim (task D3) — what a citation chip opens.
 *
 * ASKED FOR ONLY WHEN A CHIP IS CLICKED (`enabled` on the cite): a page can
 * carry dozens of citations, and resolving them on render would turn opening one
 * page into dozens of round trips for cards nobody asked to see.
 *
 * The cite id rides the path VERBATIM, encoded — it carries colons, and provider
 * ids carry worse. Nothing rebuilds it from the chip's label.
 */
export function useEvidence(cite: string | null) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  return useQuery<NamiEvidenceCard>({
    queryKey: brainKeys.evidence(userId, cite ?? ""),
    queryFn: async () =>
      getJson(
        `${SERVER_URL}/api/nami/evidence/${encodeURIComponent(cite ?? "")}`,
        await getHeaders()
      ),
    enabled: !!userId && !!cite,
    staleTime: 60_000,
    retry: retryUnlessRefused,
  });
}

/**
 * Find a page (task D5).
 *
 * KEYED BY THE QUERY, so React Query's cache is the history: backspacing to a
 * query already asked answers from memory instead of asking again. The caller
 * debounces what it hands in — this hook asks for exactly what it is given.
 *
 * An EMPTY query never leaves the browser: the server answers it with nothing by
 * design, and a round trip for that is a round trip for a box nobody has typed in.
 */
export function useBrainSearch(query: string) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  return useQuery<NamiTreeSearchResults>({
    queryKey: brainKeys.search(userId, query),
    queryFn: async () =>
      getJson(
        `${SERVER_URL}/api/nami/tree/search?q=${encodeURIComponent(query)}`,
        await getHeaders()
      ),
    enabled: !!userId && !!query,
    staleTime: 60_000,
    retry: retryUnlessRefused,
  });
}

/**
 * What points at the page being read (task D5).
 *
 * ASKED BESIDE THE PAGE, not on a click: it is one bundle-only read and the
 * answer belongs under the article the moment it renders. A page whose links
 * could not be read shows no block at all — the room, not this hook, holds that
 * rule, because the hook's job is to report what came back.
 */
export function useBrainBacklinks(path: string | null) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  return useQuery<NamiTreeBacklinks>({
    queryKey: brainKeys.backlinks(userId, path ?? ""),
    queryFn: async () =>
      getJson(
        `${SERVER_URL}/api/nami/tree/backlinks?path=${encodeURIComponent(path ?? "")}`,
        await getHeaders()
      ),
    enabled: !!userId && !!path,
    staleTime: 60_000,
    retry: retryUnlessRefused,
  });
}

// =============================================================================
// The front door (task D6/W1) — the three reads the Brain's own sections make.
// =============================================================================

/** The gallery's doors, in the order the route spells them. Sense and task are
 *  not things you look at, so the strip never asks for them. */
export const GALLERY_DOORS = "artifact,upload,web";

/**
 * The proof under one page, grouped the way the page reads (task D6/S2).
 *
 * ASKED BESIDE THE PAGE, not on a click, and that is the whole point of the
 * route: the per-cite card exists because resolving a page's citations one at a
 * time would be dozens of round trips, so this is the one request that answers
 * them all. A page with no proof and a page whose proof could not be read are
 * different answers, and only the second is worth saying out loud — the caller
 * holds that rule, because this hook's job is to report what came back.
 */
export function useBrainPageProof(path: string | null) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  return useQuery<NamiPageProof>({
    queryKey: brainDoorKeys.pageProof(userId, path ?? ""),
    queryFn: async () =>
      getJson(
        `${SERVER_URL}/api/nami/tree/page-proof?path=${encodeURIComponent(path ?? "")}`,
        await getHeaders()
      ),
    enabled: !!userId && !!path,
    staleTime: 60_000,
    retry: retryUnlessRefused,
  });
}

/**
 * Everything the brain holds that is not words (task D6/S3).
 *
 * ONE PAGE ONLY, and the caller says how big: Home shows a strip of five and the
 * gallery shows a grid. `nextCursor` rides the answer for the surface that pages
 * through it; nothing here holds a cursor, because a hook that accumulated pages
 * would be a second cache beside React Query's.
 */
export function useBrainGallery(limit: number, doors: string = GALLERY_DOORS) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  return useQuery<NamiEvidenceListing>({
    queryKey: brainDoorKeys.gallery(userId, doors, limit),
    queryFn: async () =>
      getJson(
        `${SERVER_URL}/api/nami/evidence?doors=${encodeURIComponent(doors)}&limit=${limit}`,
        await getHeaders()
      ),
    enabled: !!userId,
    staleTime: 60_000,
    retry: retryUnlessRefused,
  });
}

/**
 * THE WHOLE GALLERY, a page at a time (task D6/W2).
 *
 * `useBrainGallery` above answers ONE page and is what Home's five-tile strip
 * wants. The Proof section is a grid a person scrolls, and a brain holds hundreds
 * of these — so this is the cursor-following read, and the cursor is the route's
 * own opaque token rather than an offset the client counts.
 *
 * THE COUNTS ON THE FILTERS FOLLOW WHAT IS LOADED, which is why the caller only
 * prints them once there is no next page: a chip reading "Uploaded · 10" over a
 * grid holding the first 48 of 452 would be a number about the request rather
 * than about the brain.
 */
export function useBrainGalleryPages(
  limit: number,
  doors: string = GALLERY_DOORS
) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  return useInfiniteQuery<NamiEvidenceListing>({
    queryKey: [...brainDoorKeys.gallery(userId, doors, limit), "pages"],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) =>
      getJson(
        `${SERVER_URL}/api/nami/evidence?doors=${encodeURIComponent(doors)}&limit=${limit}` +
          (pageParam ? `&cursor=${encodeURIComponent(String(pageParam))}` : ""),
        await getHeaders()
      ),
    getNextPageParam: (last) => last.nextCursor,
    enabled: !!userId,
    staleTime: 60_000,
    retry: retryUnlessRefused,
  });
}

/**
 * What is waiting on you (task D6/S4).
 *
 * READ IN TWO PLACES AT ONCE — the band's badge and the section itself — off one
 * query key, so the number on the pill and the length of the list can never
 * disagree.
 */
export function useBrainLoops() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  return useQuery<NamiOpenLoops>({
    queryKey: brainDoorKeys.loops(userId),
    queryFn: async () =>
      getJson(`${SERVER_URL}/api/nami/loops`, await getHeaders()),
    enabled: !!userId,
    staleTime: 60_000,
    retry: retryUnlessRefused,
  });
}

/**
 * The Recent updates on your pages (Phase 4 Train 1, spec §6) — derived at publish,
 * read off the head, so it costs no wake. Dropped on every write, like the page proof.
 */
export function useBrainSelfUpdates() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  return useQuery<NamiSelfUpdates>({
    queryKey: brainDoorKeys.selfUpdates(userId),
    queryFn: async () =>
      getJson(`${SERVER_URL}/api/nami/tree/self-updates`, await getHeaders()),
    enabled: !!userId,
    staleTime: 60_000,
    retry: retryUnlessRefused,
  });
}

/**
 * A tile's picture, as something an `<img>` can actually show.
 *
 * WHY THIS IS NOT A `src`. The thumb route is behind the same bearer gate as
 * every other read on this rail, and an `<img src>` sends no Authorization
 * header — so pointing one at the URL the listing hands over gets a 401 and a
 * broken tile, every time. The bytes are fetched the way the rest of the room
 * fetches, and handed to the browser as an object URL.
 *
 * THE OBJECT URL IS OWNED BY THE COMPONENT, not by this hook: the blob is the
 * cached value, and revoking a URL minted here would break the second tile that
 * read the same cached blob. `useThumbSrc` below mints and revokes one per mount.
 *
 * A MISSING PICTURE IS NOT AN ERROR. The route answers 404 for anything it will
 * not resize — a format libvips does not read, an original too large to decode —
 * and the room draws its typed placeholder for exactly that answer.
 */
export function useThumbBlob(cite: string | null, url: string | null) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  return useQuery<Blob>({
    queryKey: brainDoorKeys.thumb(userId, cite ?? ""),
    queryFn: async () => {
      const res = await resilientFetch(`${SERVER_URL}${url}`, {
        headers: await getHeaders(),
      });
      if (!res.ok) throw new BrainReadError(res.status);
      return await res.blob();
    },
    enabled: !!userId && !!cite && !!url,
    // The bytes are immutable — the route says so in its own Cache-Control — so
    // this never goes stale within a session.
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    retry: retryUnlessRefused,
  });
}
